import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

export type ManagerTeam = {
  id: string
  name: string
  start_time: string
  end_time: string
  planned_member_count: number
  sort_order: number
}

type TeamScopeValue = {
  teams: ManagerTeam[]
  selectedTeamId: string | null
  selectedTeam: ManagerTeam | null
  setSelectedTeamId: (teamId: string | null) => void
  loadingTeams: boolean
  organisationId: string
  refreshTeams: () => void
}

const TeamScopeContext = createContext<TeamScopeValue | null>(null)

export function TeamScopeProvider({ children }: { children: ReactNode }) {
  const [teams, setTeams] = useState<ManagerTeam[]>([])
  const [selectedTeamId, setSelectedTeamIdState] = useState<string | null>(null)
  const [loadingTeams, setLoadingTeams] = useState(true)
  const [organisationId, setOrganisationId] = useState('')
  const [refreshVersion, setRefreshVersion] = useState(0)

  useEffect(() => {
    let active = true
    setLoadingTeams(true)

    async function loadTeams() {
      if (!supabase) {
        setLoadingTeams(false)
        return
      }

      const { data: authData } = await supabase.auth.getUser()
      if (!authData.user || !active) {
        setLoadingTeams(false)
        return
      }

      const { data: membership, error: membershipError } = await supabase
        .from('memberships')
        .select('organisation_id')
        .eq('user_id', authData.user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (membershipError || !membership?.organisation_id || !active) {
        setLoadingTeams(false)
        return
      }

      const orgId = String(membership.organisation_id)
      setOrganisationId(orgId)

      const { data: teamRows, error: teamsError } = await supabase
        .from('teams')
        .select('id,name,start_time,end_time,planned_member_count,sort_order')
        .eq('organisation_id', orgId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (!active) return

      if (teamsError) {
        console.warn('[Mail Percent] Could not load manager teams', teamsError)
        setLoadingTeams(false)
        return
      }

      const loadedTeams = (teamRows ?? []) as ManagerTeam[]
      setTeams(loadedTeams)

      const storageKey = `mail-percent:selected-team:${orgId}`
      const stored = window.localStorage.getItem(storageKey)
      const validStored = stored === 'all' || loadedTeams.some(team => team.id === stored)

      if (validStored) {
        setSelectedTeamIdState(stored === 'all' ? null : stored)
      } else {
        // Managers should begin at the organisation-wide view. A connected
        // mailbox can legitimately be unassigned to a team (for example a
        // manager test connection or a mailbox awaiting employee assignment).
        // Defaulting to the first team made organisation metadata appear for
        // a moment and then disappear as soon as the team scope finished loading.
        setSelectedTeamIdState(null)
        window.localStorage.setItem(storageKey, 'all')
      }

      setLoadingTeams(false)
    }

    loadTeams()

    return () => {
      active = false
    }
  }, [refreshVersion])

  function setSelectedTeamId(teamId: string | null) {
    setSelectedTeamIdState(teamId)
    if (organisationId) {
      window.localStorage.setItem(
        `mail-percent:selected-team:${organisationId}`,
        teamId ?? 'all'
      )
    }
  }

  const selectedTeam = useMemo(
    () => teams.find(team => team.id === selectedTeamId) ?? null,
    [teams, selectedTeamId]
  )

  const value = useMemo<TeamScopeValue>(() => ({
    teams,
    selectedTeamId,
    selectedTeam,
    setSelectedTeamId,
    loadingTeams,
    organisationId,
    refreshTeams: () => setRefreshVersion(value => value + 1)
  }), [teams, selectedTeamId, selectedTeam, loadingTeams, organisationId])

  return <TeamScopeContext.Provider value={value}>{children}</TeamScopeContext.Provider>
}

export function useTeamScope() {
  const value = useContext(TeamScopeContext)
  if (!value) throw new Error('useTeamScope must be used inside TeamScopeProvider.')
  return value
}
